import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  Tag,
  Popconfirm,
  Tooltip,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  AimOutlined,
} from '@ant-design/icons';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import {
  customBaseUrl,
  httpGet,
  httpPost,
  httpPut,
  httpDelete,
} from '../../store/http';

const { Option } = Select;

const TARGET_TERMS = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'];
const CATEGORIES = ['A', 'B', 'C'];

const PosTargetConfigSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [searchCategory, setSearchCategory] = useState('');
  const [form] = Form.useForm();

  const isOk = (res) =>
    res?.respCode === 0 || res?.success === true || res?.status === true;
  const getBody = (res) => res?.respBody ?? res?.data ?? res;
  const getMsg = (res) => res?.respDesc || res?.message || 'Request failed.';

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await httpGet(
        `${customBaseUrl.transactionServiceUrl}/posTargetConfig`,
      );
      if (isOk(res)) {
        const body = getBody(res);
        setConfigs(Array.isArray(body) ? body : body ? [body] : []);
      } else {
        setConfigs([]);
        NotificationManager.error(getMsg(res));
      }
    } catch (err) {
      NotificationManager.error('Error loading POS target configs.');
    } finally {
      setLoading(false);
    }
  };

  const searchByCategory = async () => {
    const q = searchCategory.trim();
    if (!q) {
      fetchAll();
      return;
    }
    setLoading(true);
    try {
      const res = await httpGet(
        `${customBaseUrl.transactionServiceUrl}/posTargetConfig/category/${encodeURIComponent(q)}`,
      );
      if (isOk(res)) {
        const body = getBody(res);
        if (Array.isArray(body)) {
          setConfigs(body);
        } else if (body && typeof body === 'object' && body.category) {
          setConfigs([body]);
        } else {
          setConfigs([]);
          NotificationManager.info('No configuration found for that category.');
        }
      } else {
        setConfigs([]);
        NotificationManager.info(getMsg(res));
      }
    } catch (err) {
      NotificationManager.error('Error searching POS target config.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openCreate = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({
      active: true,
      targetTerm: 'MONTHLY',
      penaltyFee: 0,
      volumeCount: 0,
      volumeTotal: 0,
    });
    setShowModal(true);
  };

  const openEdit = (record) => {
    setEditingConfig(record);
    form.setFieldsValue({
      active: record.active,
      category: record.category,
      description: record.description,
      penaltyFee: record.penaltyFee,
      targetTerm: record.targetTerm,
      volumeCount: record.volumeCount,
      volumeTotal: record.volumeTotal,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    const payload = {
      active: !!values.active,
      category: values.category,
      description: values.description,
      penaltyFee: Number(values.penaltyFee) || 0,
      targetTerm: values.targetTerm,
      volumeCount: Number(values.volumeCount) || 0,
      volumeTotal: Number(values.volumeTotal) || 0,
    };

    setSaving(true);
    try {
      let res;
      if (editingConfig?.id) {
        res = await httpPut(
          `${customBaseUrl.transactionServiceUrl}/posTargetConfig/${editingConfig.id}`,
          payload,
        );
      } else {
        res = await httpPost(
          `${customBaseUrl.transactionServiceUrl}/posTargetConfig`,
          payload,
          false,
          null,
          false,
        );
      }
      if (isOk(res)) {
        Swal.fire({
          title: editingConfig ? 'Updated' : 'Created',
          text: `POS target config ${editingConfig ? 'updated' : 'created'} successfully.`,
          icon: 'success',
          confirmButtonColor: '#ff6700',
        });
        closeModal();
        fetchAll();
      } else {
        NotificationManager.error(getMsg(res));
      }
    } catch (err) {
      NotificationManager.error('Error saving POS target config.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await httpDelete(
        `${customBaseUrl.transactionServiceUrl}/posTargetConfig/${id}`,
      );
      if (isOk(res)) {
        NotificationManager.success('POS target config deleted.');
        fetchAll();
      } else {
        NotificationManager.error(getMsg(res));
      }
    } catch (err) {
      NotificationManager.error('Error deleting POS target config.');
    }
  };

  const columns = [
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Target Term', dataIndex: 'targetTerm', key: 'targetTerm' },
    {
      title: 'Volume Count',
      dataIndex: 'volumeCount',
      key: 'volumeCount',
    },
    {
      title: 'Volume Total',
      dataIndex: 'volumeTotal',
      key: 'volumeTotal',
      render: (val) => (val != null ? Number(val).toLocaleString() : '-'),
    },
    {
      title: 'Penalty Fee',
      dataIndex: 'penaltyFee',
      key: 'penaltyFee',
      render: (val) => (val != null ? Number(val).toLocaleString() : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (val) => (
        <Tag color={val ? 'green' : 'red'}>{val ? 'ACTIVE' : 'INACTIVE'}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            >
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete this POS target config?"
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading} tip="Loading configurations...">
      <div style={{ padding: '8px 0' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            padding: '12px 16px',
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: 8,
          }}
        >
          <span style={{ color: '#595959', fontSize: '13px' }}>
            Manage POS target configurations keyed by merchant category.
          </span>
          <Space>
            <Input
              placeholder="Search by category"
              value={searchCategory}
              onChange={(e) => setSearchCategory(e.target.value)}
              onPressEnter={searchByCategory}
              allowClear
              onClear={fetchAll}
              style={{ width: 220 }}
            />
            <Button icon={<SearchOutlined />} onClick={searchByCategory}>
              Search
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchAll}>
              Reload
            </Button>
          </Space>
        </div>

        <Card
          title={
            <Space>
              <AimOutlined style={{ color: '#ff6700' }} />
              <span>POS Target Configurations</span>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              style={{
                background: '#ff6700',
                borderColor: '#ff6700',
                fontWeight: 600,
              }}
            >
              New Config
            </Button>
          }
          style={{
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            border: '1px solid #f0f0f0',
          }}
        >
          <Table
            dataSource={configs}
            columns={columns}
            rowKey={(record) => record.id || record.category}
            pagination={{ pageSize: 10 }}
          />
        </Card>

        <Modal
          visible={showModal}
          title={
            editingConfig ? 'Edit POS Target Config' : 'Create POS Target Config'
          }
          onCancel={closeModal}
          footer={null}
          centered
          width={640}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item
                label="Merchant Category"
                name="category"
                rules={[{ required: true, message: 'Category is required' }]}
              >
                <Select
                  placeholder="Select category"
                  disabled={!!editingConfig}
                >
                  {CATEGORIES.map((c) => (
                    <Option key={c} value={c}>
                      {c}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Target Term"
                name="targetTerm"
                rules={[{ required: true, message: 'Target term is required' }]}
              >
                <Select placeholder="Select term">
                  {TARGET_TERMS.map((t) => (
                    <Option key={t} value={t}>
                      {t}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: 'Description is required' }]}
            >
              <Input.TextArea rows={3} placeholder="Enter description" />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <Form.Item
                label="Volume Count"
                name="volumeCount"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="Volume Total"
                name="volumeTotal"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>

              <Form.Item
                label="Penalty Fee"
                name="penaltyFee"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>
            </div>

            <Form.Item
              label="Active"
              name="active"
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={closeModal}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  style={{
                    background: '#ff6700',
                    borderColor: '#ff6700',
                    fontWeight: 600,
                  }}
                >
                  {editingConfig ? 'Update' : 'Create'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Spin>
  );
};

export default PosTargetConfigSettings;
